/**
 * 購入者ルート定義（BuyerLayout）
 *
 * @file buyer-routes.tsx
 * @version 1.0.0
 * @author Japan4U Development Team
 * @summary
 * 購入者向けページのルーティング設定。
 * 認証前後の全画面を統合管理（旧Publicロール統合済み）。
 * BuyerLayoutを静的importし、40+ルートを管理。
 */
import React, { Suspense } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import ProtectedRoute from '@shared/components/ProtectedRoute';
import { lazyWithI18n } from '@shared/i18n';
import { I18nLoadingScreen } from '../../../frontend-shared/src/contexts/LanguageContext';
import BuyerLayout from '@/layouts/BuyerLayout';
import { UnifiedErrorPage } from '../components/common/UnifiedErrorPage';
import { ErrorCodes } from '../utils/errorCodes';
import { LanguageWrapper } from '../components/LanguageWrapper';

/**
 * 購入者ページ用の遅延ロードヘルパー関数
 * 全ページに自動的に layout/BuyerLayout と common namespaceを追加
 */
const lazyBuyerPage = (
  importFn: () => Promise<{ default: React.ComponentType }>,
  pageNamespaces: string[]
) => {
  return lazyWithI18n(importFn, [
    ...pageNamespaces,
    'layout/BuyerLayout',
    'common',
    'errors'
  ]);
};

// ========================================
// 公開ページ（認証不要）
// ========================================
const HomePage = lazyBuyerPage(
  () => import('../pages/HomePage'),
  ['buyer/HomePage']
);  // P-001: ホームページ

const YauctionProductListPage = lazyBuyerPage(
  () => import('../pages/YauctionProductListPage'),
  ['buyer/YauctionProductListPage']
);  // P-002A: ヤフオク商品一覧

const MercariProductListPage = lazyBuyerPage(
  () => import('../pages/MercariProductListPage'),
  ['buyer/MercariProductListPage']
);  // P-002B: メルカリ商品一覧

const NormalProductListPage = lazyBuyerPage(
  () => import('../pages/NormalProductListPage'),
  ['buyer/NormalProductListPage']
);  // P-002C: 通常商品一覧

const YauctionProductDetailPage = lazyBuyerPage(
  () => import('../pages/YauctionProductDetailPage'),
  ['buyer/YauctionProductDetailPage']
);  // P-003/U-001: ヤフオク商品詳細ページ（認証前/後）

const MercariProductDetailPage = lazyBuyerPage(
  () => import('../pages/MercariProductDetailPage'),
  ['buyer/MercariProductDetailPage']
);  // P-004/U-004: メルカリ商品詳細ページ（認証前/後）

const NormalProductDetailPage = lazyBuyerPage(
  () => import('../pages/NormalProductDetailPage'),
  ['buyer/NormalProductDetailPage']
);  // P-005/U-007: 通常商品詳細ページ（認証前/後）

const HelpPage = lazyBuyerPage(
  () => import('../pages/HelpPage'),
  ['buyer/HelpPage']
);  // P-007: FAQ・ヘルプページ

const EstimateSimulationPage = lazyBuyerPage(
  () => import('../pages/EstimateSimulationPage'),
  ['buyer/EstimateSimulationPage']
);  // P-008: 見積シミュレーション

const ReviewsPage = lazyBuyerPage(
  () => import('../pages/ReviewsPage'),
  ['buyer/ReviewsPage']
);  // P-009: レビュー

// ========================================
// 静的ページ（認証不要）
// ========================================
const CompanyPage = lazyBuyerPage(
  () => import('../pages/CompanyPage'),
  ['buyer/CompanyPage']
);  // P-010: 会社概要

const TermsPage = lazyBuyerPage(
  () => import('../pages/TermsPage'),
  ['buyer/TermsPage']
);  // P-011: 利用規約

const PrivacyPage = lazyBuyerPage(
  () => import('../pages/PrivacyPage'),
  ['buyer/PrivacyPage']
);  // P-012: プライバシーポリシー

const TokuteiPage = lazyBuyerPage(
  () => import('../pages/TokuteiPage'),
  ['buyer/TokuteiPage']
);  // P-013: 特定商取引法

const NewsPage = lazyBuyerPage(
  () => import('../pages/NewsPage'),
  ['buyer/NewsPage']
);  // P-015: ニュース

const CareersPage = lazyBuyerPage(
  () => import('../pages/CareersPage'),
  ['buyer/CareersPage']
);  // P-016: 採用情報

const PressPage = lazyBuyerPage(
  () => import('../pages/PressPage'),
  ['buyer/PressPage']
);  // P-017: プレスリリース

const ShippingPage = lazyBuyerPage(
  () => import('../pages/ShippingPage'),
  ['buyer/ShippingPage']
);  // P-018: 配送について

const ReturnsPage = lazyBuyerPage(
  () => import('../pages/ReturnsPage'),
  ['buyer/ReturnsPage']
);  // P-019: 返品・交換

const PaymentPage = lazyBuyerPage(
  () => import('../pages/PaymentPage'),
  ['buyer/PaymentPage']
);  // P-020: 支払い方法

const UserGuidePage = lazyBuyerPage(
  () => import('../pages/UserGuidePage'),
  ['buyer/UserGuidePage']
);  // P-021: ご利用ガイド

const LoginPage = lazyWithI18n(
  () => import('../pages/LoginPage'),
  ['buyer/LoginPage', 'common', 'errors']
);  // U-020: ログイン

const VerifyEmailPage = lazyWithI18n(
  () => import('../pages/VerifyEmailPage'),
  ['buyer/VerifyEmailPage', 'common', 'errors']
);  // U-021: メールアドレス確認

const ResetPasswordPage = lazyWithI18n(
  () => import('../pages/ResetPasswordPage'),
  ['buyer/ResetPasswordPage', 'common', 'errors']
);  // U-022: パスワードリセット

// ========================================
// 購入者ページ（認証必須）
// ========================================
const YauctionCheckoutPage = lazyBuyerPage(
  () => import('../pages/YauctionCheckoutPage'),
  ['buyer/YauctionCheckoutPage']
);  // U-002A: ヤフオク即時決済

const YauctionCheckoutResultPage = lazyBuyerPage(
  () => import('../pages/YauctionCheckoutResultPage'),
  ['buyer/YauctionCheckoutResultPage']
);  // U-002B: ヤフオク即時決済（完了後）

const YauctionBidPage = lazyBuyerPage(
  () => import('../pages/YauctionBidPage'),
  ['buyer/YauctionBidPage']
);  // U-003A: ヤフオク入札決済

const YauctionBidResultPage = lazyBuyerPage(
  () => import('../pages/YauctionBidResultPage'),
  ['buyer/YauctionBidResultPage']
);  // U-003B: ヤフオク入札決済（完了後）

const MercariCheckoutPage = lazyBuyerPage(
  () => import('../pages/MercariCheckoutPage'),
  ['buyer/MercariCheckoutPage']
);  // U-005A: メルカリ即時決済

const MercariCheckoutResultPage = lazyBuyerPage(
  () => import('../pages/MercariCheckoutResultPage'),
  ['buyer/MercariCheckoutResultPage']
);  // U-005B: メルカリ即時決済（完了後）

const MercariBidPage = lazyBuyerPage(
  () => import('../pages/MercariBidPage'),
  ['buyer/MercariBidPage']
);  // U-006A: メルカリ入札決済

const MercariBidResultPage = lazyBuyerPage(
  () => import('../pages/MercariBidResultPage'),
  ['buyer/MercariBidResultPage']
);  // U-006B: メルカリ入札決済（完了後）

const CartPage = lazyBuyerPage(
  () => import('../pages/CartPage'),
  ['buyer/CartPage']
);  // U-008: 通常商品カート

const CheckoutPage = lazyBuyerPage(
  () => import('../pages/CheckoutPage'),
  ['buyer/CheckoutPage']
);  // U-009A: 通常商品決済

const CheckoutResultPage = lazyBuyerPage(
  () => import('../pages/CheckoutResultPage'),
  ['buyer/CheckoutResultPage']
);  // U-009B: 通常商品決済（完了後）

// マイページ（lazyBuyerPage - 多言語化対応）
const MyPage = lazyBuyerPage(
  () => import('../pages/MyPage'),
  ['buyer/MyPage']
);  // U-010A: マイページ（ダッシュボード）

const MyPageProxyOrdersPage = lazyBuyerPage(
  () => import('../pages/MyPageProxyOrdersPage'),
  ['buyer/MyPageProxyOrdersPage']
);  // U-010B: マイページ（代理購入履歴）

const MyPageOrdersPage = lazyBuyerPage(
  () => import('../pages/MyPageOrdersPage'),
  ['buyer/MyPageOrdersPage']
);  // U-010C: マイページ（通常購入履歴）

const MyPageFormRequestPage = lazyBuyerPage(
  () => import('../pages/MyPageFormRequestPage'),
  ['buyer/MyPageFormRequestPage']
);  // U-010I: マイページ（購入代行依頼）

const MyPageShippingPage = lazyBuyerPage(
  () => import('../pages/MyPageShippingPage'),
  ['buyer/MyPageShippingPage']
);  // U-010D: マイページ（配送状況追跡）

const MyPageFavoritePage = lazyBuyerPage(
  () => import('../pages/MyPageFavoritePage'),
  ['buyer/MyPageFavoritePage']
);  // U-010E: マイページ（お気に入り管理）

const MyPageCouponPage = lazyBuyerPage(
  () => import('../pages/MyPageCouponPage'),
  ['buyer/MyPageCouponPage']
);  // U-010F: マイページ（クーポン管理）

const MyPageProfilePage = lazyBuyerPage(
  () => import('../pages/MyPageProfilePage'),
  ['buyer/MyPageProfilePage']
);  // U-010G: マイページ（個人情報管理）

const MyPageSettingsPage = lazyBuyerPage(
  () => import('../pages/MyPageSettingsPage'),
  ['buyer/MyPageSettingsPage']
);  // U-010H: マイページ（アカウント設定）

const MyPageChatSupportPage = lazyBuyerPage(
  () => import('../pages/MyPageChatSupportPage'),
  ['buyer/MyPageChatSupportPage']
);  // U-010J: マイページ（チャットサポート）

const MyPageProxyDetailPage = lazyBuyerPage(
  () => import('../pages/MyPageProxyDetailPage'),
  ['buyer/MyPageProxyDetailPage']
);  // U-010K: マイページ（代理購入詳細）

const MyPageProxyRecommendPage = lazyBuyerPage(
  () => import('../pages/MyPageProxyRecommendPage'),
  ['buyer/MyPageProxyRecommendPage']
);  // U-010L: マイページ（おすすめ代理購入）

const MyPageNotificationsPage = lazyBuyerPage(
  () => import('../pages/MyPageNotificationsPage'),
  ['buyer/MyPageNotificationsPage']
);  // U-010N: マイページ（お知らせ一覧）

// 認証不要ルートのラッパーコンポーネント
const PublicBuyerLayout: React.FC = () => {
  return <BuyerLayout />;
};

// 認証必須ルートのラッパーコンポーネント
const ProtectedBuyerLayout: React.FC = () => {
  return (
    <ProtectedRoute buyerOnly={true}>
      <BuyerLayout />
    </ProtectedRoute>
  );
};

// 旧URL（メール・通知リンク）から新パスへの転送コンポーネント
const RedirectToOrdersPaymentRequest: React.FC = () => {
  const { requestNumber } = useParams<{ requestNumber: string }>();
  return <Navigate to={`/mypage/orders/payment-requests/${requestNumber}`} replace />;
};

const BuyerRoutes: React.FC = () => {
  return (
    <Routes>
      {/* 言語プレフィックス対応ラッパー */}
      <Route path="/:lang?" element={<LanguageWrapper />}>
        {/* 認証不要の公開ルート */}
        <Route element={<PublicBuyerLayout />}>
          {/* P-001: ホームページ */}
          <Route index element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <HomePage />
            </Suspense>
          } />

          {/* P-002A: ヤフオク商品一覧 */}
          <Route path="yauction/products" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <YauctionProductListPage />
            </Suspense>
          } />

          {/* P-002B: メルカリ商品一覧 */}
          <Route path="mercari/products" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <MercariProductListPage />
            </Suspense>
          } />

          {/* P-002C: 通常商品一覧 */}
          <Route path="products" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <NormalProductListPage />
            </Suspense>
          } />

          {/* P-003/U-001: ヤフオク商品詳細（認証前/後） */}
          <Route path="yauction/products/:productCode" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <YauctionProductDetailPage />
            </Suspense>
          } />

          {/* P-004/U-004: メルカリ商品詳細（認証前/後） */}
          <Route path="mercari/products/:productCode" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <MercariProductDetailPage />
            </Suspense>
          } />

          {/* P-005/U-007: 通常商品詳細（認証前/後） */}
          <Route path="products/:productCode" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <NormalProductDetailPage />
            </Suspense>
          } />

          {/* P-007: FAQ・ヘルプページ */}
          <Route path="help" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <HelpPage />
            </Suspense>
          } />

          {/* P-008: 見積シミュレーション */}
          <Route path="estimate" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <EstimateSimulationPage />
            </Suspense>
          } />

          {/* P-009: レビュー */}
          <Route path="reviews" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <ReviewsPage />
            </Suspense>
          } />

          {/* P-010: 会社情報 */}
          <Route path="company" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <CompanyPage />
            </Suspense>
          } />

          {/* P-011: 利用規約 */}
          <Route path="terms" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <TermsPage />
            </Suspense>
          } />

          {/* P-012: プライバシーポリシー */}
          <Route path="privacy" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <PrivacyPage />
            </Suspense>
          } />

          {/* P-013: 特定商取引法に基づく表示 */}
          <Route path="tokutei" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <TokuteiPage />
            </Suspense>
          } />

          {/* P-015: ニュース */}
          <Route path="news" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <NewsPage />
            </Suspense>
          } />

          {/* P-016: 採用情報 */}
          <Route path="careers" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <CareersPage />
            </Suspense>
          } />

          {/* P-017: プレスリリース */}
          <Route path="press" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <PressPage />
            </Suspense>
          } />

          {/* P-018: 配送について */}
          <Route path="shipping" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <ShippingPage />
            </Suspense>
          } />

          {/* P-019: 返品・交換 */}
          <Route path="returns" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <ReturnsPage />
            </Suspense>
          } />

          {/* P-020: お支払い方法 */}
          <Route path="payment" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <PaymentPage />
            </Suspense>
          } />

          {/* P-021: ご利用ガイド */}
          <Route path="user-guide" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <UserGuidePage />
            </Suspense>
          } />
        </Route>

        {/* 認証必須の購入者ルート */}
        <Route element={<ProtectedBuyerLayout />}>
          {/* U-002A: ヤフオク即時決済 */}
          <Route path="yauction/checkout/:productCode" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <YauctionCheckoutPage />
            </Suspense>
          } />

          {/* U-002B: ヤフオク即時決済（完了後） */}
          <Route path="yauction/checkout/:productCode/result" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <YauctionCheckoutResultPage />
            </Suspense>
          } />

          {/* U-003A: ヤフオク入札決済 */}
          <Route path="yauction/bid/:productCode" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <YauctionBidPage />
            </Suspense>
          } />

          {/* U-003B: ヤフオク入札決済（完了後） */}
          <Route path="yauction/bid/:productCode/result" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <YauctionBidResultPage />
            </Suspense>
          } />

          {/* U-005A: メルカリ即時決済 */}
          <Route path="mercari/checkout/:productCode" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <MercariCheckoutPage />
            </Suspense>
          } />

          {/* U-005B: メルカリ即時決済（完了後） */}
          <Route path="mercari/checkout/:productCode/result" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <MercariCheckoutResultPage />
            </Suspense>
          } />

          {/* U-006A: メルカリ入札決済 */}
          <Route path="mercari/bid/:productCode" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <MercariBidPage />
            </Suspense>
          } />

          {/* U-006B: メルカリ入札決済（完了後） */}
          <Route path="mercari/bid/:productCode/result" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <MercariBidResultPage />
            </Suspense>
          } />

          {/* U-008: 通常商品カート */}
          <Route path="cart" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <CartPage />
            </Suspense>
          } />

          {/* U-009A: 通常商品決済 */}
          <Route path="checkout" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <CheckoutPage />
            </Suspense>
          } />

          {/* U-009B: 通常商品決済（完了後） */}
          <Route path="checkout/result" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <CheckoutResultPage />
            </Suspense>
          } />

          {/* U-010A: マイページ（ダッシュボード） */}
          <Route path="mypage" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <MyPage />
            </Suspense>
          } />

          {/* U-010B: マイページ（代理購入履歴） */}
          <Route path="mypage/proxy-orders" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <MyPageProxyOrdersPage />
            </Suspense>
          } />

          {/* U-010C: マイページ（通常購入履歴） */}
          <Route path="mypage/orders" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <MyPageOrdersPage />
            </Suspense>
          } />

          {/* U-010I: マイページ（購入代行依頼） */}
          <Route path="mypage/form-request" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <MyPageFormRequestPage />
            </Suspense>
          } />

          {/* U-010D: マイページ（配送状況追跡） */}
          <Route path="mypage/shipping" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <MyPageShippingPage />
            </Suspense>
          } />

          {/* U-010E: マイページ（お気に入り管理） */}
          <Route path="mypage/wishlist" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <MyPageFavoritePage />
            </Suspense>
          } />

          {/* U-010F: マイページ（クーポン管理） */}
          <Route path="mypage/coupons" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <MyPageCouponPage />
            </Suspense>
          } />

          {/* U-010G: マイページ（個人情報管理） */}
          <Route path="mypage/profile" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <MyPageProfilePage />
            </Suspense>
          } />

          {/* U-010H: マイページ（アカウント設定） */}
          <Route path="mypage/settings" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <MyPageSettingsPage />
            </Suspense>
          } />

          {/* U-010J: マイページ（チャットサポート） */}
          <Route path="mypage/chat" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <MyPageChatSupportPage />
            </Suspense>
          } />

          {/* U-010K: マイページ（代理購入詳細） */}
          <Route path="mypage/proxy-detail/:orderId" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <MyPageProxyDetailPage />
            </Suspense>
          } />

          {/* U-010C: マイページ（通常購入履歴・海外送料の二次決済モーダルを開いた状態） */}
          <Route path="mypage/orders/payment-requests/:requestNumber" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <MyPageOrdersPage />
            </Suspense>
          } />

          {/* 旧URL: メール・通知に残るリンクを新パスへ転送 */}
          <Route path="mypage/payment-requests/:requestNumber" element={<RedirectToOrdersPaymentRequest />} />

          {/* U-010L: マイページ（おすすめ代理購入） */}
          <Route path="mypage/proxy-recommend" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <MyPageProxyRecommendPage />
            </Suspense>
          } />

          {/* U-010N: マイページ（お知らせ一覧） */}
          <Route path="mypage/notifications" element={
            <Suspense fallback={<I18nLoadingScreen />}>
              <MyPageNotificationsPage />
            </Suspense>
          } />
        </Route>

        {/* U-020: ログイン */}
        <Route path="login" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <LoginPage />
          </Suspense>
        } />

        {/* U-021: メールアドレス確認 */}
        <Route path="verify-email" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <VerifyEmailPage />
          </Suspense>
        } />

        {/* U-022: パスワードリセット */}
        <Route path="reset-password" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <ResetPasswordPage />
          </Suspense>
        } />
      </Route>

      {/* 404 Not Found - 全ての未定義ルートをキャッチ */}
      <Route path="*" element={<UnifiedErrorPage errorCode={ErrorCodes.PAGE_NOT_FOUND} />} />
    </Routes>
  );
};

export default BuyerRoutes;
